import React, { useState } from 'react';
import {
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Box,
  Container,
  Alert,
  useTheme,
  useMediaQuery,
  InputAdornment,
  CircularProgress
} from '@mui/material';
import { Lock, LogIn, Activity } from 'lucide-react';
import { hashPassword, setAuthToken, generateSessionToken } from '../utils/auth';

interface LoginProps {
  onLogin: (password: string) => void;
}

export const Login: React.FC<LoginProps> = ({ onLogin }) => {
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const passwordHash = await hashPassword(password);
      const sessionToken = generateSessionToken();

      const response = await fetch('/api/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ passwordHash, sessionToken }),
      });

      const data = await response.json();

      if (!response.ok) {
        if (response.status === 401) {
          setError('Incorrect password. Please try again.');
        } else {
          setError(data.error || 'Authentication failed. Please try again.');
        }
        setLoading(false);
        return;
      }

      setAuthToken(data.signedToken);
      onLogin(password);
      setError('');
    } catch (error) {
      console.error('Authentication error:', error);
      setError('Authentication failed. Please try again.');
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 50%, #a78bfa 100%)',
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: '10%',
          left: '-10%',
          width: '50%',
          height: '50%',
          borderRadius: '50%',
          background: 'rgba(255,255,255,0.05)',
          filter: 'blur(80px)',
        },
        '&::after': {
          content: '""',
          position: 'absolute',
          bottom: '-10%',
          right: '-10%',
          width: '40%',
          height: '40%',
          borderRadius: '50%',
          background: 'rgba(16, 185, 129, 0.1)',
          filter: 'blur(80px)',
        },
      }}
    >
      <Container maxWidth="sm" sx={{ position: 'relative', zIndex: 1 }}>
        <Box sx={{ py: { xs: 3, sm: 6 } }}>
          <Card
            sx={{
              borderRadius: 4,
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
              border: 'none',
            }}
          >
            <CardContent sx={{ p: { xs: 3, sm: 5 } }}>
              <Box display="flex" flexDirection="column" alignItems="center" gap={3}>
                {/* Logo */}
                <Box
                  sx={{
                    width: { xs: 64, sm: 80 },
                    height: { xs: 64, sm: 80 },
                    borderRadius: 3,
                    background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 8px 24px rgba(99, 102, 241, 0.3)',
                    animation: 'pulse 3s ease-in-out infinite',
                    mb: 1,
                  }}
                >
                  <Activity size={isMobile ? 32 : 40} color="white" />
                </Box>

                {/* Title */}
                <Box textAlign="center">
                  <Typography
                    variant={isMobile ? 'h5' : 'h4'}
                    component="h1"
                    sx={{
                      fontWeight: 700,
                      color: 'text.primary',
                      mb: 0.5,
                    }}
                  >
                    Welcome to Stride
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                  >
                    Your personal fitness journey companion
                  </Typography>
                </Box>

                {/* Login Form */}
                <Box
                  component="form"
                  onSubmit={handleSubmit}
                  sx={{ width: '100%', mt: 1 }}
                >
                  <TextField
                    fullWidth
                    type="password"
                    label="Password"
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      if (error) setError('');
                    }}
                    error={!!error}
                    disabled={loading}
                    sx={{ mb: 2 }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Lock size={20} color="#94a3b8" />
                        </InputAdornment>
                      ),
                    }}
                    autoFocus
                  />

                  {error && (
                    <Alert
                      severity="error"
                      sx={{ mb: 2 }}
                    >
                      {error}
                    </Alert>
                  )}

                  <Button
                    fullWidth
                    variant="contained"
                    type="submit"
                    size="large"
                    disabled={loading || !password}
                    sx={{
                      mt: 1,
                      py: 1.5,
                    }}
                    startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <LogIn size={20} />}
                  >
                    {loading ? 'Signing in...' : 'Sign In'}
                  </Button>
                </Box>

                {/* Footer text */}
                <Typography
                  variant="caption"
                  color="text.secondary"
                  textAlign="center"
                  sx={{ mt: 1 }}
                >
                  Track your progress &middot; Set goals &middot; Achieve success
                </Typography>
              </Box>
            </CardContent>
          </Card>

          <Box sx={{ mt: 4, textAlign: 'center' }}>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)' }}>
              &copy; {new Date().getFullYear()} Stride Weight Tracker
            </Typography>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};
